import Tooltip from "@mui/material/Tooltip";
import React, { useMemo } from "react";
import { Field } from "react-final-form";
import Box from "@mui/material/Box";

import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import Txt from "../../typography/Txt";
import RffInputWrapper from "../shared/RffInputWrapper";

const formatValueByType = (value, type) => {
  if (isNil(value) || value === "") return value;

  try {
    switch (type) {
      case "phone":
        return formatPhoneNumber(value);
      case "dollar":
        return `$${formatDollar(value)}`;
      case "percent":
        return `${formatPercent(value)}%`;
      case "number":
        return formatNumber(value);
      case "date":
        return formatDate(value);
      default:
        return value;
    }
  } catch (error) {
    console.error(`Error formatting value as ${type}:`, error);
    return value;
  }
};

const formatPhoneNumber = (value) => {
  if (!value) return value;

  const phoneNumber = String(value).replace(/\D/g, "");

  if (phoneNumber.length === 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }

  return value;
};

const formatDollar = (value) => {
  if (isNil(value)) return value;

  const numValue = Number(value);

  if (isNaN(numValue)) return value;

  const hasCents = numValue % 1 !== 0;

  if (hasCents) {
    return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else {
    return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

const formatPercent = (value) => {
  if (isNil(value)) return value;

  const numValue = Number(value);

  if (isNaN(numValue)) return value;

  return `${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatNumber = (value) => {
  if (isNil(value)) return value;

  const numValue = Number(value);

  if (isNaN(numValue)) return value;

  return numValue.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return value;

  try {
    const date = new Date(value);

    if (isNaN(date.getTime())) return value;

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    return value;
  }
};

const RffDisplayField = React.memo(function RffDisplayField({
  name,
  label,
  value,
  emptyValue = "—",
  suppressGrid = true,
  size,
  type,
  data,
  maxWidth,
  color,
  icon,
}) {
  const cellFontSize = useMemo(() => "12px", []);
  const cellValueFontWeight = useMemo(() => 600, []);

  const valueStyles = useMemo(() => {
    return {
      fontWeight: cellValueFontWeight,
      fontSize: cellFontSize,
      color: color || "inherit",
    };
  }, [cellValueFontWeight, cellFontSize, color]);

  const ellipsisStyles = useMemo(() => {
    if (!maxWidth) return {};

    return {
      maxWidth,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      display: "block",
    };
  }, [maxWidth]);

  const iconStyles = useMemo(
    () => ({
      marginRight: "3px",
      color: color || "inherit",
      display: "inline-flex",
      alignItems: "center",
      svg: {
        fontSize: "14px",
      },
    }),
    [cellFontSize, color]
  );

  const TextWithTooltip = ({ content, styles }) => {
    const contentWithIcon = (
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        {icon && (
          <Box component="span" sx={iconStyles}>
            {icon}
          </Box>
        )}
        <Txt sx={{ ...styles, ...(maxWidth ? ellipsisStyles : {}) }}>
          {content}
        </Txt>
      </Box>
    );

    if (!maxWidth) {
      return contentWithIcon;
    }

    return (
      <Tooltip
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {icon && (
              <Box component="span" sx={{ ...iconStyles, marginRight: "4px" }}>
                {icon}
              </Box>
            )}
            {content}
          </Box>
        }
        arrow
        placement="top"
        enterDelay={200}
        enterNextDelay={200}
      >
        <div style={{ display: "inline-block", width: "100%" }}>
          {contentWithIcon}
        </div>
      </Tooltip>
    );
  };

  if (value !== undefined) {
    const displayValue =
      !isNumber(value) && !isString(value)
        ? "N/A"
        : type
          ? formatValueByType(value, type)
          : value;

    return (
      <RffInputWrapper
        label={label}
        suppressGrid={suppressGrid}
        size={size}
        fullWidth={false}
      >
        <TextWithTooltip content={displayValue} styles={valueStyles} />
      </RffInputWrapper>
    );
  }

  if (data && name && data[name] !== undefined) {
    const displayValue = type
      ? formatValueByType(data[name], type)
      : data[name];

    return (
      <RffInputWrapper
        label={label}
        suppressGrid={suppressGrid}
        size={size}
        fullWidth={false}
      >
        <TextWithTooltip content={displayValue} styles={valueStyles} />
      </RffInputWrapper>
    );
  }

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      fullWidth={false}
    >
      <Field
        name={name}
        subscription={{ value: true, error: true, touched: true }}
        render={({ input, meta }) => {
          const displayValue =
            isNil(input?.value) || input?.value === ""
              ? emptyValue
              : type
                ? formatValueByType(input.value, type)
                : input.value;

          return (
            <TextWithTooltip content={displayValue} styles={valueStyles} />
          );
        }}
      />
    </RffInputWrapper>
  );
});

export default RffDisplayField;
